<template>
  <v-dialog
    :model-value="modelValue"
    max-width="860"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="wage-ssn-missing-dialog">
      <div class="wage-ssn-missing-dialog__header">
        <div class="header-icon">
          <v-icon size="18" color="white">mdi-card-account-details-outline</v-icon>
        </div>
        <div class="header-text">
          <h2 class="header-title">주민번호·입사일 등록 후 마감할 수 있어요</h2>
          <p class="header-sub">
            <template v-if="loading">불러오는 중…</template>
            <template v-else-if="rows.length">
              <template v-if="ssnGapCount">주민번호 {{ ssnGapCount }}명</template>
              <template v-if="ssnGapCount && joinGapCount"> · </template>
              <template v-if="joinGapCount">입사일 {{ joinGapCount }}명</template>
              · 비어 있는 항목만 입력하면 됩니다
            </template>
            <template v-else>미등록자가 없습니다. 마감을 이어갈 수 있습니다</template>
          </p>
        </div>
        <button
          type="button"
          class="wage-ssn-missing-dialog__close"
          :disabled="saving"
          aria-label="닫기"
          @click="close"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="wage-ssn-missing-dialog__body">
        <p v-if="rows.length || loading || loadError" class="intro">
          임금 마감에는 현원 직원의 주민번호와 입사일이 필요합니다. 비어 있는 항목을 입력하고 저장한 뒤 마감해 주세요.
        </p>

        <div v-if="loading" class="state-box">불러오는 중…</div>
        <div v-else-if="loadError" class="state-box error">{{ loadError }}</div>
        <div v-else-if="!rows.length" class="state-box ok">
          미등록자가 모두 등록되었습니다. 「마감 계속하기」를 눌러 주세요.
        </div>
        <div v-else class="ssn-table-wrap">
          <table class="ssn-table">
            <thead>
              <tr>
                <th class="col-name">성명</th>
                <th class="col-job">직종</th>
                <th class="col-ssn">주민번호 뒷자리</th>
                <th class="col-join">입사일</th>
                <th class="col-save"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="rowKey(row)">
                <td class="col-name">
                  <span class="name">{{ row.clci_name || '-' }}</span>
                  <span class="birth">{{ formatBirth(row.clci_birth_date) }}</span>
                </td>
                <td class="col-job">{{ row.clci_job_type || '-' }}</td>
                <td class="col-ssn">
                  <div v-if="row.clci_has_ssn_back" class="field-done">등록됨</div>
                  <div v-else class="ssn-input-row">
                    <span class="ssn-front">{{ formatSsnFront(row.clci_birth_date) }}</span>
                    <span class="ssn-dash">-</span>
                    <input
                      v-model="drafts[rowKey(row)]"
                      class="ssn-back-input"
                      type="text"
                      maxlength="7"
                      inputmode="numeric"
                      autocomplete="off"
                      placeholder="7자리"
                      :disabled="saving"
                      @input="onSsnInput(rowKey(row), $event)"
                      @keydown.enter.prevent="saveOne(row)"
                    />
                  </div>
                </td>
                <td class="col-join">
                  <div v-if="hasJoinDate(row.clci_work_start_date)" class="field-done">
                    {{ formatBirth(row.clci_work_start_date) }}
                  </div>
                  <YmdDateInput
                    v-else
                    v-model="joinDrafts[rowKey(row)]"
                    :disabled="saving"
                  />
                </td>
                <td class="col-save">
                  <button
                    type="button"
                    class="ssn-save-btn"
                    :disabled="saving"
                    @click="saveOne(row)"
                  >
                    저장
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="wage-ssn-missing-dialog__footer">
        <button type="button" class="btn-secondary" :disabled="saving" @click="close">
          닫기
        </button>
        <button
          v-if="!loading && !loadError && !rows.length"
          type="button"
          class="btn-primary"
          :disabled="saving"
          @click="continueMagam"
        >
          마감 계속하기
        </button>
        <button
          v-else
          type="button"
          class="btn-primary"
          :disabled="saving || !saveableCount"
          @click="saveAll"
        >
          <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
          <template v-else>입력한 {{ saveableCount }}명 저장</template>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { employeeApi } from '@/services/api';
import YmdDateInput from '@/components/common/YmdDateInput.vue';

type MissingRow = {
  clci_idx: number;
  clci_source?: string;
  clci_name?: string;
  clci_birth_date?: string;
  clci_job_type?: string;
  clci_has_ssn_back?: boolean;
  clci_work_start_date?: string | null;
};

type SavePlan = {
  ssn?: string;
  join?: string;
  /** 저장할 값이 없고, 입력이 덜 됐을 때 */
  error?: string;
  /** 다른 항목은 저장하지만, 이 입력은 빠질 때 */
  pending?: string;
};

const props = defineProps<{
  modelValue: boolean;
  memberCode: string;
  /** YYYYMM 또는 YYYY-MM */
  yearMonth: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved'): void;
  (e: 'continue-magam'): void;
}>();

const loading = ref(false);
const saving = ref(false);
const loadError = ref('');
const rows = ref<MissingRow[]>([]);
const drafts = reactive<Record<string, string>>({});
const joinDrafts = reactive<Record<string, string>>({});

const yearMonthDash = computed(() => {
  const digits = String(props.yearMonth || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length !== 6) return '';
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}`;
});

const rowKey = (row: MissingRow) =>
  `${row.clci_source === 'manual' ? 'manual' : 'auto'}:${row.clci_idx}`;

const isValidSsnBack = (raw: string) => /^\d{7}$/.test(String(raw || '').replace(/\D/g, ''));

const hasJoinDate = (value?: string | null) => {
  const raw = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) && raw !== '0000-00-00';
};

const isValidJoinDate = (raw: string) => hasJoinDate(raw);

/** 입사일이 이 연월보다 뒤면 현원에서 빠진다 */
const leavesHyunwon = (join: string) => {
  const ym = yearMonthDash.value;
  return !!ym && join.slice(0, 7) > ym;
};

const formatBirth = (birthDate?: string | null) => {
  const raw = String(birthDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  return raw;
};

const formatSsnFront = (birthDate?: string) => {
  const raw = formatBirth(birthDate).replace(/-/g, '');
  if (raw.length !== 8) return '******';
  return raw.slice(2);
};

const ssnGapCount = computed(() => rows.value.filter((row) => !row.clci_has_ssn_back).length);
const joinGapCount = computed(
  () => rows.value.filter((row) => !hasJoinDate(row.clci_work_start_date)).length
);

const clearDrafts = () => {
  Object.keys(drafts).forEach((k) => {
    delete drafts[k];
  });
  Object.keys(joinDrafts).forEach((k) => {
    delete joinDrafts[k];
  });
};

const needsGap = (row: MissingRow) =>
  !!row?.clci_idx && (!row.clci_has_ssn_back || !hasJoinDate(row.clci_work_start_date));

const planFor = (row: MissingRow): SavePlan => {
  const key = rowKey(row);
  const plan: SavePlan = {};
  const pending: string[] = [];
  if (!row.clci_has_ssn_back) {
    const ssn = String(drafts[key] || '').replace(/\D/g, '');
    if (ssn && !isValidSsnBack(ssn)) pending.push('주민번호 뒷자리는 숫자 7자리여야 합니다.');
    else if (isValidSsnBack(ssn)) plan.ssn = ssn;
  }
  if (!hasJoinDate(row.clci_work_start_date)) {
    const join = String(joinDrafts[key] || '').trim().slice(0, 10);
    if (join && !isValidJoinDate(join)) pending.push('입사일은 YYYY-MM-DD로 입력해 주세요.');
    else if (isValidJoinDate(join)) plan.join = join;
  }
  if (pending.length) {
    if (plan.ssn || plan.join) plan.pending = pending[0];
    else plan.error = pending[0];
  }
  return plan;
};

const canSaveRow = (row: MissingRow) => {
  const plan = planFor(row);
  return !plan.error && !!(plan.ssn || plan.join);
};

const saveableCount = computed(() => rows.value.filter((row) => canSaveRow(row)).length);

const loadRows = async () => {
  if (!props.memberCode || !yearMonthDash.value) {
    rows.value = [];
    loadError.value = '시설·연월 정보가 없습니다.';
    return;
  }
  loading.value = true;
  loadError.value = '';
  clearDrafts();
  try {
    const res = await employeeApi.getList(props.memberCode, {
      yearMonth: yearMonthDash.value,
      status: '현원',
      limit: 5000,
      preferJoinDate: true,
    });
    const list = (res.data?.data?.list || []) as MissingRow[];
    rows.value = list.filter((row) => needsGap(row));
    rows.value.forEach((row) => {
      const key = rowKey(row);
      drafts[key] = '';
      joinDrafts[key] = '';
    });
  } catch (error: any) {
    rows.value = [];
    loadError.value =
      error.response?.data?.message || error.message || '미등록 목록을 불러오지 못했습니다.';
  } finally {
    loading.value = false;
  }
};

const onSsnInput = (key: string, event: Event) => {
  const input = event.target as HTMLInputElement;
  drafts[key] = String(input.value || '').replace(/\D/g, '').slice(0, 7);
};

const applySaved = (key: string, plan: SavePlan) => {
  rows.value = rows.value.flatMap((row) => {
    if (rowKey(row) !== key) return [row];
    const next: MissingRow = { ...row };
    if (plan.ssn) {
      next.clci_has_ssn_back = true;
      delete drafts[key];
    }
    if (plan.join) {
      next.clci_work_start_date = plan.join;
      delete joinDrafts[key];
      if (leavesHyunwon(plan.join)) return [];
    }
    if (next.clci_has_ssn_back && hasJoinDate(next.clci_work_start_date)) return [];
    return [next];
  });
};

const persistRow = async (row: MissingRow, plan: SavePlan) => {
  const data: Record<string, string> = {};
  if (plan.ssn) data.clci_ssn_back = plan.ssn;
  if (plan.join) data.clci_work_start_date = plan.join;
  const source = row.clci_source === 'manual' ? 'manual' : 'auto';
  await employeeApi.update(props.memberCode, row.clci_idx, data, source);
  applySaved(rowKey(row), plan);
};

const saveOne = async (row: MissingRow) => {
  const plan = planFor(row);
  if (plan.error) {
    alert(plan.error);
    return;
  }
  if (!plan.ssn && !plan.join) {
    alert('주민번호 뒷자리 또는 입사일을 입력해 주세요.');
    return;
  }
  if (!props.memberCode || saving.value) return;

  saving.value = true;
  try {
    await persistRow(row, plan);
    emit('saved');
    if (plan.pending) alert(`${plan.pending}\n나머지 항목은 저장했습니다.`);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '저장에 실패했습니다.';
    alert(`저장 실패: ${message}`);
  } finally {
    saving.value = false;
  }
};

const saveAll = async () => {
  if (!props.memberCode || saving.value) return;
  const targets = rows.value.filter((row) => canSaveRow(row));
  if (!targets.length) {
    const partial = rows.value.find((row) => planFor(row).error);
    alert(partial ? planFor(partial).error : '저장할 주민번호 또는 입사일을 입력해 주세요.');
    return;
  }

  saving.value = true;
  let ok = 0;
  const failed: string[] = [];
  try {
    for (const row of targets) {
      const plan = planFor(row);
      try {
        await persistRow(row, plan);
        ok += 1;
      } catch {
        failed.push(row.clci_name || String(row.clci_idx));
      }
    }
    if (ok) emit('saved');
    const partials = rows.value.filter((row) => planFor(row).error);
    if (failed.length || partials.length) {
      const bits = [`${ok}명 저장`];
      if (failed.length) bits.push(`${failed.length}명 실패 (${failed.slice(0, 8).join(', ')})`);
      if (partials.length) bits.push(`${partials.length}명은 아직 입력이 덜 된 항목이 있습니다`);
      alert(bits.join('\n'));
    }
  } finally {
    saving.value = false;
  }
};

const close = () => {
  if (saving.value) return;
  emit('update:modelValue', false);
};

const continueMagam = () => {
  if (saving.value) return;
  emit('continue-magam');
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) void loadRows();
  }
);
</script>
